// 覆蓋檔案：ui/services/demoSeedBService.ts
// ✅ Demo(B) 資料植入（完全走前端 Firestore 寫入；附件改用 repo 內 public/demo/B 的靜態 PDF 檔）
// 目的：
// - 不依賴 functions build
// - 每筆附件都是真不同檔案（不共用 demo.pdf / demo.docx）
// - 讓主要功能頁（合約審理 / 用印申請 / 公文函覆 / 制式合約範本 / 案件與爭議 / 品牌資產 / 智財資產 / 法遵情資）都能直接看到 3~5 筆 demo

import { db, serverTimestamp } from "../../firebaseClient";
import type { AttachmentMeta } from "../types/attachments";
import firebase from "firebase/compat/app";
import { syncContractExpiryEvents } from "./calendarService";

export type SeedResult = {
  ok: boolean;
  counts: Record<string, number>;
  ids: Record<string, string[]>;
  message?: string;
};

function slugId(input: string) {
  return String(input)
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9\-_]/g, "")
    .slice(0, 64);
}

async function upsertDoc(collectionName: string, id: string, data: any) {
  const docId = slugId(id);
  await db.collection(collectionName).doc(docId).set(
    {
      ...data,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return docId;
}

/**
 * ✅ 靜態 PDF 附件（放在 repo 根目錄的 public/demo/B/）
 * - Vite/Cloudflare 會把 public 直接當作靜態根目錄
 * - 因此 URL 只要用 /demo/B/xxx.pdf
 */
function pdfAttachment(fileBaseNoExt: string, displayName?: string): AttachmentMeta {
  const base = String(fileBaseNoExt).trim();
  const file = base.endsWith(".pdf") ? base : `${base}.pdf`;
  return {
    name: displayName || file,
    url: `/demo/B/${file}`,
    contentType: "application/pdf",
    size: 0,
  };
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function toYmd(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function ymdToLocalDate(ymd: string) {
  const [y, m, d] = ymd.split("-").map((v) => Number(v));
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
}

// ---- ApprovalFlow helpers (寫入 approvalFlows 集合，讓「簽核流程」可直接檢視) ----
type StageKey = "LEGAL_REVIEW" | "MANAGER_OPS" | "MANAGER_GM";
function buildThreeStageFlow(params: {
  flowId: string;
  module: "CONTRACT_REVIEW" | "SEAL_REQUEST" | "OFFICIAL_REPLY";
  targetId: string;
  createdByEmail: string;
  createdDept?: string;
  status?: "IN_PROGRESS" | "APPROVED" | "REJECTED" | "RETURNED" | "DRAFT";
  currentStageKey?: StageKey;
  stage1Status?: "PENDING" | "APPROVED" | "REJECTED" | "RETURNED";
}) {
  const {
    flowId,
    module,
    targetId,
    createdByEmail,
    createdDept,
    status = "IN_PROGRESS",
    currentStageKey = "LEGAL_REVIEW",
    stage1Status = "APPROVED",
  } = params;

  const stage = (
    key: StageKey,
    label: string,
    approverName: string,
    statusVal: "PENDING" | "APPROVED" | "REJECTED" | "RETURNED",
    decision?: any
  ) => ({
    key,
    label,
    approverRole: key,
    approverName,
    status: statusVal,
    decision: decision || null,
    decidedAt: statusVal === "PENDING" ? null : new Date().toISOString(),
    comment: statusVal === "PENDING" ? "" : "（DEMO）",
  });

  return {
    id: flowId,
    module,
    targetId,
    status,
    currentStageKey,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: createdByEmail,
    createdDept: createdDept || "legal",
    stages: [
      stage(
        "LEGAL_REVIEW",
        "法務審查",
        "法務審查",
        stage1Status,
        stage1Status === "APPROVED" ? { action: "approve" } : null
      ),
      stage(
        "MANAGER_OPS",
        "主管核決（財務長）",
        "財務長",
        currentStageKey === "MANAGER_OPS" ? "PENDING" : "PENDING"
      ),
      stage(
        "MANAGER_GM",
        "主管核決（總經理）",
        "總經理",
        "PENDING"
      ),
    ],
  };
}

export async function seedDemoDataBNow(): Promise<SeedResult> {
  const counts: Record<string, number> = {};
  const ids: Record<string, string[]> = {};

  const bump = (k: string, id?: string) => {
    counts[k] = (counts[k] || 0) + 1;
    if (id) {
      ids[k] = ids[k] || [];
      ids[k].push(id);
    }
  };

  try {
    const now = new Date();
    const day = toYmd(now);

    // =====================
    // Calendar alignment: make sure auto expiry reminders (60/30/當日)
    // are visibly distributed within the current month.
    // =====================
    const y = now.getFullYear();
    const m = now.getMonth();
    const dim = new Date(y, m + 1, 0).getDate();
    const pick = (n: number) => Math.min(Math.max(n, 1), dim);

    const reminder60Date = new Date(y, m, pick(5));
    const reminder30Date = new Date(y, m, pick(13));
    const expiryDateInMonth = new Date(y, m, pick(22));

    const endFor60 = new Date(reminder60Date);
    endFor60.setDate(endFor60.getDate() + 60);
    const endFor30 = new Date(reminder30Date);
    endFor30.setDate(endFor30.getDate() + 30);
    const endFor0 = new Date(expiryDateInMonth);

    const endYmd60 = toYmd(endFor60);
    const endYmd30 = toYmd(endFor30);
    const endYmd0 = toYmd(endFor0);

    // (Optional) also generate sign-date events inside this month
    const sign1 = toYmd(new Date(y, m, pick(2)));
    const sign2 = toYmd(new Date(y, m, pick(4)));
    const sign3 = toYmd(new Date(y, m, pick(6)));

    // =====================
    // Cross-link sets (至少兩組：合約/用印/品牌資產/民事案件 可勾稽)
    // =====================
    const linkA = {
      contractNo: "CT-2026-0001",
      sealNo: "SEAL-2026-0001",
      brandNo: "BA-2026-0001",
      caseNo: "CV-2026-0001",
      disputeNo: "DP-2026-0001",
    };
    const linkB = {
      contractNo: "CT-2026-0002",
      sealNo: "SEAL-2026-0002",
      brandNo: "BA-2026-0002",
      caseNo: "CV-2026-0002",
      disputeNo: "DP-2026-0002",
    };

    // =====================
    // 1) 合約管理（legal_contracts）
    // =====================
    const contracts = [
      {
        id: "DEMO_B_legal_contract_01",
        docNo: linkA.contractNo,
        contractNo: linkA.contractNo,
        title: "平台服務／技術維運合約（DEMO）",
        counterparty: "星河科技股份有限公司",
        ownerDept: "it",
        status: "ACTIVE",
        riskLevel: "MED",
        amount: 1200000,
        currency: "TWD",
        signDate: sign1,
        startDate: sign1,
        endDate: endYmd0,
        periodStart: sign1,
        periodEnd: endYmd0,
        expiryDate: endYmd0,
        summary: `關聯：${linkA.sealNo} / ${linkA.brandNo} / ${linkA.caseNo}`,
        attachments: [pdfAttachment(linkA.contractNo)],
        relatedNos: {
          contractNo: linkA.contractNo,
          sealNo: linkA.sealNo,
          brandAssetNo: linkA.brandNo,
          caseNo: linkA.caseNo,
          disputeNo: linkA.disputeNo,
        },
      },
      {
        id: "DEMO_B_legal_contract_02",
        docNo: linkB.contractNo,
        contractNo: linkB.contractNo,
        title: "品牌聯名／行銷合作合約（DEMO）",
        counterparty: "晨曦創意有限公司",
        ownerDept: "mk",
        status: "ACTIVE",
        riskLevel: "HIGH",
        amount: 680000,
        currency: "TWD",
        signDate: sign2,
        startDate: sign2,
        endDate: endYmd30,
        periodStart: sign2,
        periodEnd: endYmd30,
        expiryDate: endYmd30,
        summary: `關聯：${linkB.sealNo} / ${linkB.brandNo} / ${linkB.caseNo}`,
        attachments: [pdfAttachment(linkB.contractNo)],
        relatedNos: {
          contractNo: linkB.contractNo,
          sealNo: linkB.sealNo,
          brandAssetNo: linkB.brandNo,
          caseNo: linkB.caseNo,
          disputeNo: linkB.disputeNo,
        },
      },
      {
        id: "DEMO_B_legal_contract_03",
        docNo: "CT-2026-0003",
        contractNo: "CT-2026-0003",
        title: "物流／倉儲服務合約（DEMO）",
        counterparty: "北港物流股份有限公司",
        ownerDept: "ops",
        status: "DRAFT",
        riskLevel: "LOW",
        amount: 350000,
        currency: "TWD",
        signDate: sign3,
        startDate: sign3,
        endDate: endYmd60,
        periodStart: sign3,
        periodEnd: endYmd60,
        expiryDate: endYmd60,
        summary: "試用資料（第三筆）",
        attachments: [pdfAttachment("CT-2026-0003")],
      },
    ];
    for (const c of contracts) {
      const id = await upsertDoc("legal_contracts", c.id, {
        ...c,
        createdAt: serverTimestamp(),
      });
      bump("legal_contracts", id);

      // Create/refresh calendar auto-events for this contract
      await syncContractExpiryEvents({
        id,
        contractNo: (c as any).contractNo || (c as any).docNo,
        title: c.title,
        periodEnd: (c as any).periodEnd || (c as any).endDate,
        signDate: (c as any).signDate || (c as any).startDate,
      });
    }


    // =====================
    // Calendar manual events (DEMO): spread within this month
    // =====================
    const manualEvents = [
      {
        id: "DEMO_B_manual_event_01",
        date: toYmd(new Date(y, m, pick(8))),
        title: "月度法遵例會（DEMO）",
        note: "示範事件（可刪除）",
      },
      {
        id: "DEMO_B_manual_event_02",
        date: toYmd(new Date(y, m, pick(16))),
        title: "用印批次整理（DEMO）",
        note: `關聯用印：${linkA.sealNo} / ${linkB.sealNo}`,
      },
      {
        id: "DEMO_B_manual_event_03",
        date: toYmd(new Date(y, m, pick(26))),
        title: "品牌資產上架檢核（DEMO）",
        note: `關聯品牌：${linkA.brandNo} / ${linkB.brandNo}`,
      },
    ];
    for (const e of manualEvents) {
      const ts = firebase.firestore.Timestamp.fromDate(ymdToLocalDate(e.date));
      const id = await upsertDoc("calendarEvents", e.id, {
        title: e.title,
        startDate: e.date,
        endDate: e.date,
        date: ts,
        isAllDay: true,
        mode: "ALL_DAY_RANGE",
        recurrence: "NONE",
        sourceType: "manual",
        sourceId: "",
        note: e.note,
        createdBy: "kc@lg.app",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      bump("calendarEvents", id);
    }
    // =====================
    // 2) 舊合約登錄（contracts / legacyContracts）
    // =====================
    const repoMain = [
      {
        id: "DEMO_B_repo_contract_01",
        contractNo: linkA.contractNo,
        title: "平台服務／技術維運合約（登錄 DE MO）",
        counterparty: "星河科技股份有限公司",
        periodStart: day,
        periodEnd: endYmd0,
        status: "ACTIVE",
        dept: "IT",
        notes: `對應用印：${linkA.sealNo}；對應品牌：${linkA.brandNo}；對應案件：${linkA.caseNo}`,
        attachments: [pdfAttachment(linkA.contractNo, `${linkA.contractNo}.pdf`)],
        relatedNos: {
          contractNo: linkA.contractNo,
          sealNo: linkA.sealNo,
          brandAssetNo: linkA.brandNo,
          caseNo: linkA.caseNo,
        },
      },
      {
        id: "DEMO_B_repo_contract_02",
        contractNo: linkB.contractNo,
        title: "品牌聯名／行銷合作合約（登錄 DEMO）",
        counterparty: "晨曦創意有限公司",
        periodStart: day,
        periodEnd: endYmd30,
        status: "ACTIVE",
        dept: "MK",
        notes: `對應用印：${linkB.sealNo}；對應品牌：${linkB.brandNo}；對應案件：${linkB.caseNo}`,
        attachments: [pdfAttachment(linkB.contractNo, `${linkB.contractNo}.pdf`)],
        relatedNos: {
          contractNo: linkB.contractNo,
          sealNo: linkB.sealNo,
          brandAssetNo: linkB.brandNo,
          caseNo: linkB.caseNo,
        },
      },
      {
        id: "DEMO_B_repo_contract_03",
        contractNo: "CT-2024-0137",
        title: "商品採購合約（登錄 DEMO）",
        counterparty: "海風供應鏈有限公司",
        periodStart: "2024-06-01",
        periodEnd: "2025-06-01",
        status: "EXPIRED",
        dept: "SCM",
        notes: "歷史合約樣本",
        attachments: [pdfAttachment("CT-2024-0137")],
      },
    ];
    for (const it of repoMain) {
      const id = await upsertDoc("contracts", it.id, {
        ...it,
        createdAt: serverTimestamp(),
      });
      bump("contracts", id);
    }

    const repoLegacy = [
      {
        id: "DEMO_B_repo_legacy_01",
        contractNo: "LC-2018-0042",
        title: "代工／OEM 合約（Legacy DEMO）",
        counterparty: "東港製造股份有限公司",
        periodStart: "2018-04-01",
        periodEnd: "2020-04-01",
        status: "ARCHIVED",
        dept: "OPS",
        notes: "legacyContracts 範例 01",
        attachments: [pdfAttachment("LC-2018-0042")],
        isLegacy: true,
      },
      {
        id: "DEMO_B_repo_legacy_02",
        contractNo: "LC-2019-0099",
        title: "租賃合約（Legacy DEMO）",
        counterparty: "新湖資產管理有限公司",
        periodStart: "2019-09-01",
        periodEnd: "2022-09-01",
        status: "ARCHIVED",
        dept: "GA",
        notes: "legacyContracts 範例 02",
        attachments: [pdfAttachment("LC-2019-0099")],
        isLegacy: true,
      },
      {
        id: "DEMO_B_repo_legacy_03",
        contractNo: "LC-2020-0021",
        title: "經銷／代理合約（Legacy DEMO）",
        counterparty: "北辰通路有限公司",
        periodStart: "2020-02-01",
        periodEnd: "2023-02-01",
        status: "ARCHIVED",
        dept: "SALES",
        notes: "legacyContracts 範例 03",
        attachments: [pdfAttachment("LC-2020-0021")],
        isLegacy: true,
      },
    ];
    for (const it of repoLegacy) {
      const id = await upsertDoc("legacyContracts", it.id, {
        ...it,
        createdAt: serverTimestamp(),
      });
      bump("legacyContracts", id);
    }

    // =====================
    // 3) 制式合約範本（contract_templates）
    // =====================
    const templates = [
      {
        id: "DEMO_B_template_01",
        title: "B2B 服務合約範本（DEMO）",
        docCode: "TMP-2026-0001",
        description: "適用：技術維運／平台服務（含 SLA 與資安條款）",
        targetAudience: "企業法務／資訊部",
        contractType: "服務合約",
        departments: ["IT", "LEGAL"],
        attachments: [pdfAttachment("TMP-2026-0001")],
        status: "APPROVED",
      },
      {
        id: "DEMO_B_template_02",
        title: "品牌合作／聯名合約範本（DEMO）",
        docCode: "TMP-2026-0002",
        description: "適用：品牌聯名、授權、聯合行銷（含素材責任與審稿流程）",
        targetAudience: "行銷／品牌部",
        contractType: "合作合約",
        departments: ["MK", "LEGAL"],
        attachments: [pdfAttachment("TMP-2026-0002")],
        status: "APPROVED",
      },
      {
        id: "DEMO_B_template_03",
        title: "物流／倉儲服務合約範本（DEMO）",
        docCode: "TMP-2026-0003",
        description: "適用：物流配送、倉儲、退換貨（含賠償與KPI）",
        targetAudience: "營運／供應鏈",
        contractType: "物流合約",
        departments: ["OPS", "SCM", "LEGAL"],
        attachments: [pdfAttachment("TMP-2026-0003")],
        status: "APPROVED",
      },
    ];
    for (const t of templates) {
      const id = await upsertDoc("contract_templates", t.id, {
        ...t,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastUpdatedBy: "kc@lg.app",
      });
      bump("contract_templates", id);
    }

    // =====================
    // 4) 品牌資產（brand_assets）
    // =====================
    const brandAssets = [
      {
        id: "DEMO_B_brand_asset_01",
        assetNo: linkA.brandNo,
        name: "晨光奶茶｜品牌代理權（DEMO）",
        group: "品牌代理",
        type: "品牌授權",
        region: "TW",
        status: "ACTIVE",
        owner: "品牌部",
        expiryDate: `${new Date(now.getFullYear() + 1, now.getMonth(), now.getDate()).toISOString().slice(0, 10)}`,
        notes: `對應合約：${linkA.contractNo}；對應用印：${linkA.sealNo}；對應案件：${linkA.caseNo}`,
        attachments: [pdfAttachment(linkA.brandNo)],
        relatedNos: {
          contractNo: linkA.contractNo,
          sealNo: linkA.sealNo,
          caseNo: linkA.caseNo,
        },
      },
      {
        id: "DEMO_B_brand_asset_02",
        assetNo: linkB.brandNo,
        name: "海島薯片｜聯名活動（DEMO）",
        group: "聯名合作",
        type: "聯名",
        region: "TW",
        status: "ACTIVE",
        owner: "行銷部",
        expiryDate: `${new Date(now.getFullYear(), now.getMonth() + 6, now.getDate()).toISOString().slice(0, 10)}`,
        notes: `對應合約：${linkB.contractNo}；對應用印：${linkB.sealNo}；對應案件：${linkB.caseNo}`,
        attachments: [pdfAttachment(linkB.brandNo)],
        relatedNos: {
          contractNo: linkB.contractNo,
          sealNo: linkB.sealNo,
          caseNo: linkB.caseNo,
        },
      },
      {
        id: "DEMO_B_brand_asset_03",
        assetNo: "BA-2026-0003",
        name: "自有品牌｜識別系統（DEMO）",
        group: "自有品牌",
        type: "商標／CI",
        region: "TW",
        status: "ACTIVE",
        owner: "品牌部",
        expiryDate: `${new Date(now.getFullYear() + 2, now.getMonth(), now.getDate()).toISOString().slice(0, 10)}`,
        notes: "第三筆品牌資產樣本",
        attachments: [pdfAttachment("BA-2026-0003")],
      },
    ];
    for (const a of brandAssets) {
      const id = await upsertDoc("brand_assets", a.id, {
        ...a,
        createdAt: serverTimestamp(),
      });
      bump("brand_assets", id);
    }

    // =====================
    // 5) 智財資產（ip_assets）
    // =====================
    const ipAssets = [
      {
        id: "DEMO_B_ip_asset_01",
        ipNo: "IP-2026-0001",
        title: "商標：晨光奶茶（DEMO）",
        category: "TRADEMARK",
        status: "REGISTERED",
        ownerDept: "legal",
        dueDate: `${new Date(now.getFullYear() + 10, now.getMonth(), now.getDate()).toISOString().slice(0, 10)}`,
        notes: `對應品牌：${linkA.brandNo} / 對應合約：${linkA.contractNo}`,
        attachments: [pdfAttachment("IP-2026-0001")],
        relatedNos: {
          brandAssetNo: linkA.brandNo,
          contractNo: linkA.contractNo,
        },
      },
      {
        id: "DEMO_B_ip_asset_02",
        ipNo: "IP-2026-0002",
        title: "著作權：行銷素材包（DEMO）",
        category: "COPYRIGHT",
        status: "PENDING",
        ownerDept: "mk",
        dueDate: `${new Date(now.getFullYear(), now.getMonth() + 2, now.getDate()).toISOString().slice(0, 10)}`,
        notes: `對應品牌：${linkB.brandNo} / 對應合約：${linkB.contractNo}`,
        attachments: [pdfAttachment("IP-2026-0002")],
        relatedNos: {
          brandAssetNo: linkB.brandNo,
          contractNo: linkB.contractNo,
        },
      },
      {
        id: "DEMO_B_ip_asset_03",
        ipNo: "IP-2026-0003",
        title: "專利：封裝結構改良（DEMO）",
        category: "PATENT",
        status: "DRAFT",
        ownerDept: "rd",
        dueDate: `${new Date(now.getFullYear(), now.getMonth() + 4, now.getDate()).toISOString().slice(0, 10)}`,
        notes: "第三筆智財資產樣本",
        attachments: [pdfAttachment("IP-2026-0003")],
      },
    ];
    for (const a of ipAssets) {
      const id = await upsertDoc("ip_assets", a.id, {
        ...a,
        createdAt: serverTimestamp(),
      });
      bump("ip_assets", id);
    }

    // =====================
    // 6) 案件與爭議（legal_cases / legal_disputes / legal_matters）
    // =====================
    const legalCases = [
      {
        id: "DEMO_B_case_01",
        caseNo: linkA.caseNo,
        title: "給付貨款爭議（DEMO 民事）",
        court: "臺灣臺北地方法院",
        status: "IN_PROGRESS",
        ownerDept: "legal",
        filedDate: day,
        relatedContractNo: linkA.contractNo,
        notes: `關聯：${linkA.contractNo} / ${linkA.sealNo} / ${linkA.brandNo}`,
        attachments: [pdfAttachment(linkA.caseNo)],
        relatedNos: {
          contractNo: linkA.contractNo,
          sealNo: linkA.sealNo,
          brandAssetNo: linkA.brandNo,
          caseNo: linkA.caseNo,
          disputeNo: linkA.disputeNo,
        },
      },
      {
        id: "DEMO_B_case_02",
        caseNo: linkB.caseNo,
        title: "不當得利返還（DEMO 民事）",
        court: "臺灣新北地方法院",
        status: "IN_PROGRESS",
        ownerDept: "legal",
        filedDate: day,
        relatedContractNo: linkB.contractNo,
        notes: `關聯：${linkB.contractNo} / ${linkB.sealNo} / ${linkB.brandNo}`,
        attachments: [pdfAttachment(linkB.caseNo)],
        relatedNos: {
          contractNo: linkB.contractNo,
          sealNo: linkB.sealNo,
          brandAssetNo: linkB.brandNo,
          caseNo: linkB.caseNo,
          disputeNo: linkB.disputeNo,
        },
      },
      {
        id: "DEMO_B_case_03",
        caseNo: "CV-2026-0003",
        title: "損害賠償（DEMO 民事）",
        court: "臺灣士林地方法院",
        status: "DRAFT",
        ownerDept: "legal",
        filedDate: day,
        notes: "第三筆案件樣本",
        attachments: [pdfAttachment("CV-2026-0003")],
      },
    ];
    for (const c of legalCases) {
      const id = await upsertDoc("legal_cases", c.id, {
        ...c,
        createdAt: serverTimestamp(),
      });
      bump("legal_cases", id);
    }

    const disputes = [
      {
        id: "DEMO_B_dispute_01",
        disputeNo: linkA.disputeNo,
        title: "交付瑕疵／退換貨爭議（DEMO）",
        status: "OPEN",
        ownerDept: "cs",
        openedAt: day,
        notes: `對應案件：${linkA.caseNo} / 對應合約：${linkA.contractNo}`,
        attachments: [pdfAttachment(linkA.disputeNo)],
        relatedNos: {
          contractNo: linkA.contractNo,
          caseNo: linkA.caseNo,
          disputeNo: linkA.disputeNo,
        },
      },
      {
        id: "DEMO_B_dispute_02",
        disputeNo: linkB.disputeNo,
        title: "廣告素材侵權疑慮（DEMO）",
        status: "OPEN",
        ownerDept: "mk",
        openedAt: day,
        notes: `對應案件：${linkB.caseNo} / 對應合約：${linkB.contractNo}`,
        attachments: [pdfAttachment(linkB.disputeNo)],
        relatedNos: {
          contractNo: linkB.contractNo,
          caseNo: linkB.caseNo,
          disputeNo: linkB.disputeNo,
        },
      },
      {
        id: "DEMO_B_dispute_03",
        disputeNo: "DP-2026-0003",
        title: "供應延遲爭議（DEMO）",
        status: "DRAFT",
        ownerDept: "scm",
        openedAt: day,
        notes: "第三筆爭議樣本",
        attachments: [pdfAttachment("DP-2026-0003")],
      },
    ];
    for (const d of disputes) {
      const id = await upsertDoc("legal_disputes", d.id, {
        ...d,
        createdAt: serverTimestamp(),
      });
      bump("legal_disputes", id);
    }

    const matters = [
      {
        id: "DEMO_B_matter_01",
        matterNo: "LM-2026-0001",
        title: "供應商合規詢問（DEMO）",
        status: "OPEN",
        ownerDept: "legal",
        openedAt: day,
        notes: `關聯合約：${linkA.contractNo}`,
        attachments: [pdfAttachment("LM-2026-0001")],
      },
      {
        id: "DEMO_B_matter_02",
        matterNo: "LM-2026-0002",
        title: "個資事件通報評估（DEMO）",
        status: "OPEN",
        ownerDept: "legal",
        openedAt: day,
        notes: "第三方名單外洩疑慮，需做通報判斷",
        attachments: [pdfAttachment("LM-2026-0002")],
      },
      {
        id: "DEMO_B_matter_03",
        matterNo: "LM-2026-0003",
        title: "商標近似檢索（DEMO）",
        status: "DRAFT",
        ownerDept: "legal",
        openedAt: day,
        notes: "第三筆法律事項樣本",
        attachments: [pdfAttachment("LM-2026-0003")],
      },
    ];
    for (const m of matters) {
      const id = await upsertDoc("legal_matters", m.id, {
        ...m,
        createdAt: serverTimestamp(),
      });
      bump("legal_matters", id);
    }

    // =====================
    // 7) 法遵情資（compliance_cards）— 必須符合 ui/types/compliance.ts 的結構
    // =====================
    const complianceCards = [
      {
        id: "DEMO_B_compliance_card_01",
        type: "intel",
        status: "DRAFT",
        ownerDept: "pdpc-prep",
        ownerUser: "kc@lg.app",
        sectionA: {
          dateText: day,
          title: "個資籌備處｜指引草案更新（DEMO）",
          subject: "個資事件通報門檻與流程",
          summary: "摘要：更新通報期限與應備資料清單（DEMO）。",
          announcementUrl: "",
          source: { agencyCode: "pdpc-prep", agencyName: "個資籌備處" },
          attachments: [pdfAttachment("COM-INT-2026-0001")],
        },
        sectionB: {
          riskLevel: "HIGH",
          riskTags: ["個資", "通報", "資安"],
          impactedDepts: ["IT", "CS", "LEGAL"],
          suggestedActions: "1) 盤點事件分級\n2) 建立通報SOP\n3) 演練通報流程",
          legalNotes: "（DEMO）依公司內規先做風險評估再決定通報。",
          attachments: [pdfAttachment("COM-INT-2026-0001-B")],
        },
        coAssistSections: [
          {
            deptCode: "it",
            deptName: "資訊部",
            dueDate: `${new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7).toISOString().slice(0, 10)}`,
            note: "請提供現行資安事件分級與通報窗口",
            attachments: [pdfAttachment("COM-INT-2026-0001-IT")],
          },
        ],
      },
      {
        id: "DEMO_B_compliance_card_02",
        type: "intel",
        status: "DRAFT",
        ownerDept: "moea",
        ownerUser: "kc@lg.app",
        sectionA: {
          dateText: day,
          title: "經濟部｜商品標示抽查要點（DEMO）",
          subject: "電商平台商品資訊揭露",
          summary: "摘要：重點在於產地、成分、保存期限等資訊揭露（DEMO）。",
          announcementUrl: "",
          source: { agencyCode: "moea", agencyName: "經濟部" },
          attachments: [pdfAttachment("COM-INT-2026-0002")],
        },
        sectionB: {
          riskLevel: "MEDIUM",
          riskTags: ["商品標示", "電商"],
          impactedDepts: ["MK", "EC", "CS"],
          suggestedActions: "1) 上架欄位強制檢核\n2) 抽查機制\n3) 供應商責任條款",
          legalNotes: "（DEMO）需同步調整上架SOP與合約條款。",
          attachments: [pdfAttachment("COM-INT-2026-0002-B")],
        },
        coAssistSections: [],
      },
      {
        id: "DEMO_B_compliance_card_03",
        type: "project",
        status: "DRAFT",
        ownerDept: "moda",
        ownerUser: "kc@lg.app",
        sectionA: {
          dateText: day,
          title: "數位發展部｜平台治理專案（DEMO）",
          subject: "第三方賣家管理與申訴處理",
          summary: "摘要：建立賣家分級管理與申訴處理KPI（DEMO）。",
          announcementUrl: "",
          source: { agencyCode: "moda", agencyName: "數位發展部" },
          attachments: [pdfAttachment("COM-INT-2026-0003")],
        },
        sectionB: {
          riskLevel: "LOW",
          riskTags: ["平台治理"],
          impactedDepts: ["EC", "CS"],
          suggestedActions: "1) 建立賣家管理規範\n2) 申訴工單化\n3) SLA監控",
          legalNotes: "（DEMO）先用內規 + 合約條款落地。",
          attachments: [pdfAttachment("COM-INT-2026-0003-B")],
        },
        coAssistSections: [],
      },
    ];
    for (const c of complianceCards) {
      const id = await upsertDoc("compliance_cards", c.id, {
        ...c,
        createdAt: serverTimestamp(),
      });
      bump("compliance_cards", id);
    }

    // =====================
    // 8) 專案管控（compliance_governance_projects_v2）
    // =====================
    const projects = [
      {
        id: "DEMO_B_gov_prj_01",
        title: "電商平台個資盤點專案（DEMO）",
        ownerDept: "pdpc-prep",
        ownerUser: "kc@lg.app",
        status: "DRAFT",
        riskLevel: "HIGH",
        dueDate: `${new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30).toISOString().slice(0, 10)}`,
        description: "盤點蒐集/處理/保存/刪除流程，補齊內規與對外告知。",
        source_detail: {
          source: { agencyCode: "pdpc-prep", agencyName: "個資籌備處" },
          content: { title: "（DEMO）個資指引草案" },
          crawledAt: new Date().toISOString(),
        },
        primaryAttachments: [pdfAttachment("PRJ-2026-0001")],
        legalAttachments: [pdfAttachment("PRJ-2026-0001-L")],
      },
      {
        id: "DEMO_B_gov_prj_02",
        title: "商品標示抽查因應（DEMO）",
        ownerDept: "moea",
        ownerUser: "kc@lg.app",
        status: "DRAFT",
        riskLevel: "MEDIUM",
        dueDate: `${new Date(now.getFullYear(), now.getMonth(), now.getDate() + 21).toISOString().slice(0, 10)}`,
        description: "建立上架資訊檢核與供應商責任切割。",
        source_detail: {
          source: { agencyCode: "moea", agencyName: "經濟部" },
          content: { title: "（DEMO）商品標示抽查要點" },
          crawledAt: new Date().toISOString(),
        },
        primaryAttachments: [pdfAttachment("PRJ-2026-0002")],
        legalAttachments: [pdfAttachment("PRJ-2026-0002-L")],
      },
      {
        id: "DEMO_B_gov_prj_03",
        title: "平台治理內規更新（DEMO）",
        ownerDept: "moda",
        ownerUser: "kc@lg.app",
        status: "DRAFT",
        riskLevel: "LOW",
        dueDate: `${new Date(now.getFullYear(), now.getMonth(), now.getDate() + 14).toISOString().slice(0, 10)}`,
        description: "賣家管理、申訴處理、違規下架與申復流程。",
        source_detail: {
          source: { agencyCode: "moda", agencyName: "數位發展部" },
          content: { title: "（DEMO）平台治理指引" },
          crawledAt: new Date().toISOString(),
        },
        primaryAttachments: [pdfAttachment("PRJ-2026-0003")],
        legalAttachments: [pdfAttachment("PRJ-2026-0003-L")],
      },
    ];
    for (const p of projects) {
      const id = await upsertDoc("compliance_governance_projects_v2", p.id, {
        ...p,
        createdAt: serverTimestamp(),
      });
      bump("compliance_governance_projects_v2", id);
    }

    // =====================
    // 9) 律師資料/委任（legal_lawyers / lawyer_requests）
    // =====================
    const lawyers = [
      {
        id: "DEMO_B_lawyer_01",
        name: "林怡君",
        firm: "睿言法律事務所",
        email: "iyc.lin@law.demo",
        phone: "02-2333-0001",
        specialties: ["民事訴訟", "商務合約"],
        status: "ACTIVE",
        createdAt: serverTimestamp(),
      },
      {
        id: "DEMO_B_lawyer_02",
        name: "陳柏翰",
        firm: "北辰國際法律事務所",
        email: "bohan.chen@law.demo",
        phone: "02-2333-0002",
        specialties: ["個資", "資安"],
        status: "ACTIVE",
        createdAt: serverTimestamp(),
      },
      {
        id: "DEMO_B_lawyer_03",
        name: "王子晴",
        firm: "海風法律事務所",
        email: "zq.wang@law.demo",
        phone: "02-2333-0003",
        specialties: ["智財", "品牌授權"],
        status: "ACTIVE",
        createdAt: serverTimestamp(),
      },
    ];
    for (const l of lawyers) {
      const id = await upsertDoc("legal_lawyers", l.id, l);
      bump("legal_lawyers", id);
    }

    const lawyerRequests = [
      {
        id: "DEMO_B_lawyer_req_01",
        lawyerId: "DEMO_B_lawyer_01",
        requestTitle: "民事案件策略討論（DEMO）",
        requestSummary: `案件：${linkA.caseNo}（含證據整理與出庭策略）`,
        requiredHours: 8,
        hourlyRate: 6500,
        estimatedFee: 52000,
        status: "DRAFT",
        attachments: [pdfAttachment("LAW-REQ-2026-0001")],
        createdAt: serverTimestamp(),
      },
      {
        id: "DEMO_B_lawyer_req_02",
        lawyerId: "DEMO_B_lawyer_02",
        requestTitle: "個資事件通報判斷（DEMO）",
        requestSummary: "通報門檻、通知內容、對外說明的風險把關",
        requiredHours: 6,
        hourlyRate: 7000,
        estimatedFee: 42000,
        status: "DRAFT",
        attachments: [pdfAttachment("LAW-REQ-2026-0002")],
        createdAt: serverTimestamp(),
      },
      {
        id: "DEMO_B_lawyer_req_03",
        lawyerId: "DEMO_B_lawyer_03",
        requestTitle: "品牌授權條款檢視（DEMO）",
        requestSummary: `品牌：${linkB.brandNo}（授權範圍／素材責任／審稿權）`,
        requiredHours: 5,
        hourlyRate: 6500,
        estimatedFee: 32500,
        status: "DRAFT",
        attachments: [pdfAttachment("LAW-REQ-2026-0003")],
        createdAt: serverTimestamp(),
      },
    ];
    for (const r of lawyerRequests) {
      const id = await upsertDoc("lawyer_requests", r.id, r);
      bump("lawyer_requests", id);
    }

    // =====================
    // 10) 簽核用資料（approval_requests + approvalFlows）
    //     - 合約審理：module=contractDraft
    //     - 用印申請：module=sealRequest
    //     - 公文函覆：module=OFFICIAL_REPLY
    // =====================

    // 合約審理 3筆
    const cr = [
      {
        id: "DEMO_B_cr_01",
        module: "contractDraft",
        status: "IN_REVIEW",
        docCode: "CR-2026-0001",
        title: "合約審理：平台維運（DEMO）",
        requester: "kc@lg.app",
        requesterDept: "legal",
        summary: `對應合約：${linkA.contractNo}`,
        attachments: [pdfAttachment("CR-2026-0001")],
        approvalFlowId: "CONTRACT_REVIEW_DEMO_B_cr_01",
      },
      {
        id: "DEMO_B_cr_02",
        module: "contractDraft",
        status: "IN_REVIEW",
        docCode: "CR-2026-0002",
        title: "合約審理：品牌聯名（DEMO）",
        requester: "kc@lg.app",
        requesterDept: "legal",
        summary: `對應合約：${linkB.contractNo}`,
        attachments: [pdfAttachment("CR-2026-0002")],
        approvalFlowId: "CONTRACT_REVIEW_DEMO_B_cr_02",
      },
      {
        id: "DEMO_B_cr_03",
        module: "contractDraft",
        status: "DRAFT",
        docCode: "CR-2026-0003",
        title: "合約審理：物流服務（DEMO）",
        requester: "kc@lg.app",
        requesterDept: "legal",
        summary: "第三筆合約審理樣本",
        attachments: [pdfAttachment("CR-2026-0003")],
        approvalFlowId: "CONTRACT_REVIEW_DEMO_B_cr_03",
      },
    ];
    for (const it of cr) {
      const id = await upsertDoc("approval_requests", it.id, {
        ...it,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      bump("approval_requests", id);

      const flow = buildThreeStageFlow({
        flowId: it.approvalFlowId,
        module: "CONTRACT_REVIEW",
        targetId: it.id,
        createdByEmail: "kc@lg.app",
        createdDept: "legal",
        currentStageKey: "MANAGER_OPS",
        stage1Status: it.status === "DRAFT" ? "PENDING" : "APPROVED",
      });
      const flowId = await upsertDoc("approvalFlows", flow.id, flow);
      bump("approvalFlows", flowId);
    }

    // 用印申請 3筆
    const seals = [
      {
        id: "DEMO_B_seal_01",
        module: "sealRequest",
        status: "IN_REVIEW",
        docCode: linkA.sealNo,
        title: "用印申請：平台維運合約（DEMO）",
        requester: "kc@lg.app",
        requesterDept: "legal",
        sealType: "公司大小章",
        summary: `對應合約：${linkA.contractNo}；對應品牌：${linkA.brandNo}`,
        attachments: [pdfAttachment(linkA.sealNo)],
        approvalFlowId: "SEAL_REQUEST_DEMO_B_seal_01",
      },
      {
        id: "DEMO_B_seal_02",
        module: "sealRequest",
        status: "IN_REVIEW",
        docCode: linkB.sealNo,
        title: "用印申請：品牌聯名合約（DEMO）",
        requester: "kc@lg.app",
        requesterDept: "legal",
        sealType: "負責人小章",
        summary: `對應合約：${linkB.contractNo}；對應品牌：${linkB.brandNo}`,
        attachments: [pdfAttachment(linkB.sealNo)],
        approvalFlowId: "SEAL_REQUEST_DEMO_B_seal_02",
      },
      {
        id: "DEMO_B_seal_03",
        module: "sealRequest",
        status: "DRAFT",
        docCode: "SEAL-2026-0003",
        title: "用印申請：物流服務合約（DEMO）",
        requester: "kc@lg.app",
        requesterDept: "legal",
        sealType: "公司大小章",
        summary: "第三筆用印申請樣本",
        attachments: [pdfAttachment("SEAL-2026-0003")],
        approvalFlowId: "SEAL_REQUEST_DEMO_B_seal_03",
      },
    ];
    for (const it of seals) {
      const id = await upsertDoc("approval_requests", it.id, {
        ...it,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      bump("approval_requests", id);

      const flow = buildThreeStageFlow({
        flowId: it.approvalFlowId,
        module: "SEAL_REQUEST",
        targetId: it.id,
        createdByEmail: "kc@lg.app",
        createdDept: "legal",
        currentStageKey: "MANAGER_OPS",
        stage1Status: it.status === "DRAFT" ? "PENDING" : "APPROVED",
      });
      const flowId = await upsertDoc("approvalFlows", flow.id, flow);
      bump("approvalFlows", flowId);
    }

    // 公文函覆 3筆（含 INBOUND/OUTBOUND）
    const govs = [
      {
        id: "DEMO_B_gov_01",
        module: "OFFICIAL_REPLY",
        direction: "INBOUND",
        status: "IN_REVIEW",
        docNo: "GOVR-2026-0001",
        fromOrg: "某主管機關",
        toUnit: "某電子商務有限公司",
        ownerDept: "legal",
        handler: "kc@lg.app",
        subject: "來文：個資通報資料補正（DEMO）",
        summary: "要求補正通報內容與佐證文件",
        impact: "可能影響對外通報時程",
        urgency: "URGENT",
        secrecy: "NORMAL",
        attachments: [pdfAttachment("GOVR-2026-0001")],
        approvalFlowId: "OFFICIAL_REPLY_DEMO_B_gov_01",
      },
      {
        id: "DEMO_B_gov_02",
        module: "OFFICIAL_REPLY",
        direction: "OUTBOUND",
        status: "IN_REVIEW",
        docNo: "GOVR-2026-0002",
        fromOrg: "某電子商務有限公司",
        toUnit: "某主管機關",
        ownerDept: "legal",
        handler: "kc@lg.app",
        subject: "函覆：補正資料說明（DEMO）",
        summary: "回覆補正項目與附件清單",
        impact: "降低裁罰風險",
        urgency: "NORMAL",
        secrecy: "CONFIDENTIAL",
        relatedInboundNo: "GOVR-2026-0001",
        attachments: [pdfAttachment("GOVR-2026-0002")],
        approvalFlowId: "OFFICIAL_REPLY_DEMO_B_gov_02",
      },
      {
        id: "DEMO_B_gov_03",
        module: "OFFICIAL_REPLY",
        direction: "OUTBOUND",
        status: "DRAFT",
        docNo: "GOVR-2026-0003",
        fromOrg: "某電子商務有限公司",
        toUnit: "某主管機關",
        ownerDept: "legal",
        handler: "kc@lg.app",
        subject: "函覆：檢附改善計畫（DEMO）",
        summary: "提出改善計畫與內控措施",
        impact: "第三筆公文函覆樣本",
        urgency: "NORMAL",
        secrecy: "NORMAL",
        attachments: [pdfAttachment("GOVR-2026-0003")],
        approvalFlowId: "OFFICIAL_REPLY_DEMO_B_gov_03",
      },
    ];
    for (const it of govs) {
      const id = await upsertDoc("approval_requests", it.id, {
        ...it,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      bump("approval_requests", id);

      const flow = buildThreeStageFlow({
        flowId: it.approvalFlowId,
        module: "OFFICIAL_REPLY",
        targetId: it.id,
        createdByEmail: "kc@lg.app",
        createdDept: "legal",
        currentStageKey: "MANAGER_OPS",
        stage1Status: it.status === "DRAFT" ? "PENDING" : "APPROVED",
      });
      const flowId = await upsertDoc("approvalFlows", flow.id, flow);
      bump("approvalFlows", flowId);
    }

    // --- DEMO(B) Seed v2 (Additive) --------------------------------------------
    // 注意：依使用者指示「不動舊 seed，只新增」—此段僅在尚未存在 DEMO_B2 開頭的第一筆時才會寫入
    // 18 筆「真案件」待辦：財務長 11、總經理 7（含紅/黃/綠滯留天數分布）
    await seedDemoB2ApprovalTodos({ bump });


    return {
      ok: true,
      counts,
      ids,
      message: "Demo(B) 已寫入 Firestore（附件為 repo/public/demo/B 的靜態 PDF）",
    };
  } catch (err: any) {
    console.error("seedDemoDataBNow failed", err);
    return {
      ok: false,
      counts,
      ids,
      message: err?.message || String(err),
    };
  }
}


// =============================================================================
// DEMO(B) Seed v2 (Additive)
// - 不動舊 seed：只新增 DEMO_B2_* docs
// - 18 筆「真案件」：財務長 11、總經理 7（紅/黃/綠依滯留天數分布）
// =============================================================================

type DemoB2Assignee = "OPS" | "GM";

type DemoB2Module = {
  module: "contractDraft" | "sealRequest" | "OFFICIAL_REPLY";
  moduleKey: "CONTRACT_REVIEW" | "SEAL_REQUEST" | "OFFICIAL_REPLY";
  docNoPrefix: "CR" | "SEAL" | "GOVR";
};

type DemoB2Plan = DemoB2Module & {
  id: string; // Firestore doc id
  docNo: string; // CR-2026-0101
  title: string;
  assignee: DemoB2Assignee;
  ageDays: number; // 用於 KPI 分桶（0-3 / 4-6 / 7+）
};

function b2DateDaysAgo(days: number): Date {
  const d = new Date();
  d.setHours(12, 0, 0, 0); // 固定在中午，避免 DST/時區跨日
  d.setDate(d.getDate() - days);
  return d;
}

function b2Ts(days: number): firebase.firestore.Timestamp {
  return firebase.firestore.Timestamp.fromDate(b2DateDaysAgo(days));
}

function b2Iso(days: number): string {
  return b2DateDaysAgo(days).toISOString();
}

function buildThreeStageFlowV2(args: {
  flowId: string;
  moduleKey: "CONTRACT_REVIEW" | "SEAL_REQUEST" | "OFFICIAL_REPLY";
  targetId: string;
  createdByEmail: string;
  createdDept: string;
  assignee: DemoB2Assignee;
  ageDays: number;
}): any {
  const baseIso = b2Iso(args.ageDays);
  const legalActedAt = baseIso;
  const opsActedAt = baseIso;

  const stages = [
    {
      key: "LEGAL_REVIEW",
      label: "法務審查",
      status: "APPROVED",
      actorEmail: args.createdByEmail,
      actorName: "法務",
      actorDept: args.createdDept,
      actedAt: legalActedAt,
      comment: "DEMO(B2) 法務已審查",
    },
    {
      key: "MANAGER_OPS",
      label: "主管核決（財務長）",
      status: args.assignee === "GM" ? "APPROVED" : "PENDING",
      actorEmail: args.assignee === "GM" ? "cfo@lg.app" : undefined,
      actorName: args.assignee === "GM" ? "財務長" : undefined,
      actorDept: args.assignee === "GM" ? "management" : undefined,
      actedAt: args.assignee === "GM" ? opsActedAt : undefined,
      comment: args.assignee === "GM" ? "DEMO(B2) 財務長已核決" : "",
    },
    {
      key: "MANAGER_GM",
      label: "主管核決（總經理）",
      status: args.assignee === "GM" ? "PENDING" : "NOT_STARTED",
      comment: "",
    },
  ];

  const currentStageKey = args.assignee === "GM" ? "MANAGER_GM" : "MANAGER_OPS";

  return {
    id: args.flowId,
    module: args.moduleKey,
    moduleKey: args.moduleKey,
    targetId: args.targetId,
    createdByEmail: args.createdByEmail,
    createdByName: "法務",
    createdDept: args.createdDept,
    createdAt: b2Ts(args.ageDays),
    updatedAt: b2Ts(args.ageDays),
    status: "IN_PROGRESS",
    currentStageKey,
    stages,
  };
}

function buildDemoB2Plans(): DemoB2Plan[] {
  // OPS: 11（紅2 / 黃5 / 綠4）
  // GM : 7（紅1 / 黃2 / 綠4）
  const plans: DemoB2Plan[] = [
    // ---------------- OPS (紅) ----------------
    {
      id: "DEMO_B2_CR_2026_0101",
      module: "contractDraft",
      moduleKey: "CONTRACT_REVIEW",
      docNoPrefix: "CR",
      docNo: "CR-2026-0101",
      title: "年度代言合作合約（追加素材責任條款）",
      assignee: "OPS",
      ageDays: 9,
    },
    {
      id: "DEMO_B2_SEAL_2026_0101",
      module: "sealRequest",
      moduleKey: "SEAL_REQUEST",
      docNoPrefix: "SEAL",
      docNo: "SEAL-2026-0101",
      title: "對外發文用印（緊急）",
      assignee: "OPS",
      ageDays: 10,
    },

    // ---------------- OPS (黃) ----------------
    {
      id: "DEMO_B2_CR_2026_0102",
      module: "contractDraft",
      moduleKey: "CONTRACT_REVIEW",
      docNoPrefix: "CR",
      docNo: "CR-2026-0102",
      title: "供應商服務合約（保密/資安條款調整）",
      assignee: "OPS",
      ageDays: 4,
    },
    {
      id: "DEMO_B2_CR_2026_0103",
      module: "contractDraft",
      moduleKey: "CONTRACT_REVIEW",
      docNoPrefix: "CR",
      docNo: "CR-2026-0103",
      title: "KOL 合作合約（付款節點改為驗收後）",
      assignee: "OPS",
      ageDays: 5,
    },
    {
      id: "DEMO_B2_SEAL_2026_0102",
      module: "sealRequest",
      moduleKey: "SEAL_REQUEST",
      docNoPrefix: "SEAL",
      docNo: "SEAL-2026-0102",
      title: "合作備忘錄用印（對方已催）",
      assignee: "OPS",
      ageDays: 4,
    },
    {
      id: "DEMO_B2_SEAL_2026_0103",
      module: "sealRequest",
      moduleKey: "SEAL_REQUEST",
      docNoPrefix: "SEAL",
      docNo: "SEAL-2026-0103",
      title: "年度採購框架合約用印",
      assignee: "OPS",
      ageDays: 6,
    },
    {
      id: "DEMO_B2_GOVR_2026_0101",
      module: "OFFICIAL_REPLY",
      moduleKey: "OFFICIAL_REPLY",
      docNoPrefix: "GOVR",
      docNo: "GOVR-2026-0101",
      title: "主管機關來文回覆草案（期限將近）",
      assignee: "OPS",
      ageDays: 5,
    },

    // ---------------- OPS (綠) ----------------
    {
      id: "DEMO_B2_CR_2026_0104",
      module: "contractDraft",
      moduleKey: "CONTRACT_REVIEW",
      docNoPrefix: "CR",
      docNo: "CR-2026-0104",
      title: "電商平台上架條款（退貨運費責任）",
      assignee: "OPS",
      ageDays: 1,
    },
    {
      id: "DEMO_B2_SEAL_2026_0104",
      module: "sealRequest",
      moduleKey: "SEAL_REQUEST",
      docNoPrefix: "SEAL",
      docNo: "SEAL-2026-0104",
      title: "投標文件用印（一般）",
      assignee: "OPS",
      ageDays: 2,
    },
    {
      id: "DEMO_B2_GOVR_2026_0102",
      module: "OFFICIAL_REPLY",
      moduleKey: "OFFICIAL_REPLY",
      docNoPrefix: "GOVR",
      docNo: "GOVR-2026-0102",
      title: "函覆補正說明（資訊補正）",
      assignee: "OPS",
      ageDays: 0,
    },
    {
      id: "DEMO_B2_GOVR_2026_0103",
      module: "OFFICIAL_REPLY",
      moduleKey: "OFFICIAL_REPLY",
      docNoPrefix: "GOVR",
      docNo: "GOVR-2026-0103",
      title: "回覆附件補充（財會佐證）",
      assignee: "OPS",
      ageDays: 3,
    },

    // ========================================================================
    // GM: 7（紅1 / 黃2 / 綠4）
    // ========================================================================
    // ---------------- GM (紅) ----------------
    {
      id: "DEMO_B2_CR_2026_0201",
      module: "contractDraft",
      moduleKey: "CONTRACT_REVIEW",
      docNoPrefix: "CR",
      docNo: "CR-2026-0201",
      title: "重大合作合約（獨家條款）",
      assignee: "GM",
      ageDays: 9,
    },

    // ---------------- GM (黃) ----------------
    {
      id: "DEMO_B2_SEAL_2026_0201",
      module: "sealRequest",
      moduleKey: "SEAL_REQUEST",
      docNoPrefix: "SEAL",
      docNo: "SEAL-2026-0201",
      title: "策略合作案用印（需總經理核決）",
      assignee: "GM",
      ageDays: 4,
    },
    {
      id: "DEMO_B2_CR_2026_0202",
      module: "contractDraft",
      moduleKey: "CONTRACT_REVIEW",
      docNoPrefix: "CR",
      docNo: "CR-2026-0202",
      title: "外包服務合約（責任上限與賠償）",
      assignee: "GM",
      ageDays: 6,
    },

    // ---------------- GM (綠) ----------------
    {
      id: "DEMO_B2_GOVR_2026_0201",
      module: "OFFICIAL_REPLY",
      moduleKey: "OFFICIAL_REPLY",
      docNoPrefix: "GOVR",
      docNo: "GOVR-2026-0201",
      title: "公文函覆（備查版本）",
      assignee: "GM",
      ageDays: 1,
    },
    {
      id: "DEMO_B2_GOVR_2026_0202",
      module: "OFFICIAL_REPLY",
      moduleKey: "OFFICIAL_REPLY",
      docNoPrefix: "GOVR",
      docNo: "GOVR-2026-0202",
      title: "公文函覆（法遵聲明）",
      assignee: "GM",
      ageDays: 2,
    },
    {
      id: "DEMO_B2_GOVR_2026_0203",
      module: "OFFICIAL_REPLY",
      moduleKey: "OFFICIAL_REPLY",
      docNoPrefix: "GOVR",
      docNo: "GOVR-2026-0203",
      title: "公文函覆（附件補上）",
      assignee: "GM",
      ageDays: 3,
    },
    {
      id: "DEMO_B2_SEAL_2026_0202",
      module: "sealRequest",
      moduleKey: "SEAL_REQUEST",
      docNoPrefix: "SEAL",
      docNo: "SEAL-2026-0202",
      title: "年度預算核准文件用印",
      assignee: "GM",
      ageDays: 0,
    },
  ];

  return plans;
}

async function seedDemoB2ApprovalTodos(args: { bump: (collectionName: string, id: string) => void }): Promise<void> {
  // 以「第一筆 DEMO_B2_*」作為 marker，避免覆寫/重播
  const markerId = "DEMO_B2_CR_2026_0101";
  const markerSnap = await db.collection("approval_requests").doc(markerId).get();
  if (markerSnap.exists) return;

  const plans = buildDemoB2Plans();

  for (const p of plans) {
    const createdTs = b2Ts(p.ageDays);
    const flowDocId = `${p.moduleKey}_${p.id}`;

    // --- approval_requests ---
    const reqData: any = {
      id: p.id,
      module: p.module,
      moduleKey: p.moduleKey,
      docNo: p.docNo,
      docCode: p.docNo,
      title: p.title,
      status: "IN_REVIEW",
      createdAt: createdTs,
      updatedAt: createdTs,
      submittedAt: createdTs,
      createdBy: "kc@lg.app",
      createdByName: "法務",
      createdDept: "legal",
      // 讓各功能頁的附件（OCR PDF）能打得開
      attachments: [
        {
          name: `${p.docNo}.pdf`,
          // 靜態 PDF：放在 repo 根目錄 public/demo/B/（URL: /demo/B/xxx.pdf）
          url: `/demo/B/${p.docNo}.pdf`,
          contentType: "application/pdf",
        },
      ],
      approvalFlowId: flowDocId,
      // 一點點真實感
      counterparty: p.docNoPrefix === "CR" ? "某某公司" : undefined
    };

    await db.collection("approval_requests").doc(p.id).set(reqData, { merge: true });
    args.bump("approval_requests", p.id);

    // --- approvalFlows ---
    const flowData = buildThreeStageFlowV2({
      flowId: flowDocId,
      moduleKey: p.moduleKey,
      targetId: p.id,
      createdByEmail: "kc@lg.app",
      createdDept: "legal",
      assignee: p.assignee,
      ageDays: p.ageDays,
    });

    await db.collection("approvalFlows").doc(flowDocId).set(flowData, { merge: true });
    args.bump("approvalFlows", flowDocId);
  }
}

